import { StackLayout } from '@euler/app/components/StackLayout';
import { Typography } from '@euler/app/components/typography';
import { Center } from '@euler/components/Center';
import { StatusColors } from '@euler/components/Colors';
import { FontFamily } from '@euler/components/typography';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { QuotationForm } from '@euler/model/task-detail';
import { memo, useMemo } from 'react';
import { StyleSheet } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ReadonlyDiscountButton } from './DiscountButton';

const to_maybe_decimal = (value: number | null | undefined) =>
  value == null || value === -1 ? undefined : value / 100;

const to_decimal = (value: number) => value / 100;

export const ReadonlyQuotationFormFooter = memo(
  ({ form, onPrint }: { form: QuotationForm; onPrint?: () => void }) => {
    const { rpx } = useResponsiveContext();
    const styles = useStyles();
    const subjectCount = form.subjects.filter(x => x.included).length;

    const priceInfo = useMemo(() => {
      let originalTotal = 0;
      let discountedTotal = 0;
      let totalDiscount = 0;
      let originalManHourCostTotal = 0;
      let originalMaterialTotal = 0;
      let discountedManHourCostTotal = 0;
      let discountedMaterialTotal = 0;
      let computedTotal = 0;

      for (const subject of form.subjects) {
        if (!subject.included) continue;

        let itemCount = 0;

        for (const item of subject.items) {
          if (!item.included) continue;

          const itemPrice = to_maybe_decimal(item.price);
          const itemDiscountPrice = to_maybe_decimal(item.discountPrice);

          if (itemPrice == null) {
            return null;
          }

          itemCount++;

          originalTotal += itemPrice;
          discountedTotal += itemDiscountPrice ?? itemPrice;
          originalManHourCostTotal += itemPrice;
          discountedManHourCostTotal += itemDiscountPrice ?? itemPrice;
          if (itemDiscountPrice != null && itemDiscountPrice <= itemPrice) {
            totalDiscount += itemPrice - itemDiscountPrice;
          }
          for (const material of item.materials) {
            if (!material.included) continue;

            const materialPrice = to_maybe_decimal(material.price);
            const materialDiscountPrice = to_maybe_decimal(
              material.discountPrice,
            );

            if (materialPrice == null) {
              return null;
            }
            const qty = material.qty ?? 1;
            originalTotal += materialPrice * qty;
            discountedTotal += (materialDiscountPrice ?? materialPrice) * qty;
            originalMaterialTotal += materialPrice * qty;
            discountedMaterialTotal +=
              (materialDiscountPrice ?? materialPrice) * qty;
            if (
              materialDiscountPrice != null &&
              materialPrice != null &&
              materialDiscountPrice <= materialPrice
            ) {
              totalDiscount += (materialPrice - materialDiscountPrice) * qty;
            }
          }
        }

        if (itemCount === 0) {
          return null;
        }

        if (subject.additionalManHourCostSubTotalDiscount != null) {
          discountedTotal -= to_decimal(
            subject.additionalManHourCostSubTotalDiscount,
          );
          totalDiscount += to_decimal(
            subject.additionalManHourCostSubTotalDiscount,
          );
          discountedManHourCostTotal -= to_decimal(
            subject.additionalManHourCostSubTotalDiscount,
          );
        }

        if (subject.additionalMaterialSubTotalDiscount != null) {
          discountedTotal -= to_decimal(
            subject.additionalMaterialSubTotalDiscount,
          );
          totalDiscount += to_decimal(
            subject.additionalMaterialSubTotalDiscount,
          );
          discountedMaterialTotal -= to_decimal(
            subject.additionalMaterialSubTotalDiscount,
          );
        }

        if (subject.additionalSubTotalDiscount != null) {
          discountedTotal -= to_decimal(subject.additionalSubTotalDiscount);
          totalDiscount += to_decimal(subject.additionalSubTotalDiscount);
        }
      }

      computedTotal = discountedTotal;

      if (form.additionalTotalFeeDiscount != null) {
        computedTotal -= to_decimal(form.additionalTotalFeeDiscount);
        totalDiscount += to_decimal(form.additionalTotalFeeDiscount);
      }

      return {
        computedTotal,
        originalTotal,
        discountedTotal,
        totalDiscount,
        originalManHourCostTotal,
        originalMaterialTotal,
        discountedManHourCostTotal,
        discountedMaterialTotal,
      };
    }, [form.additionalTotalFeeDiscount, form.subjects]);

    return (
      <StackLayout
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        style={styles.container}
        css={`
          box-shadow: 0 0 ${rpx(15)}px rgba(0, 0, 0, 0.25);
        `}
      >
        <StackLayout>
          <StackLayout direction="row" alignItems="center" gap={rpx(4)}>
            <Typography.Text
              style={styles.totalLabel}
            >{`已选${subjectCount}项, 合计:`}</Typography.Text>
            <Typography.Text style={styles.total}>{`￥${
              priceInfo == null ? '--' : priceInfo.computedTotal.toFixed(2)
            }`}</Typography.Text>
            {priceInfo != null && (
              <ReadonlyDiscountButton
                price={priceInfo.originalTotal}
                discountPrice={priceInfo.discountedTotal}
                discount={to_decimal(form.additionalTotalFeeDiscount)}
              />
            )}
          </StackLayout>
          <StackLayout direction="row" alignItems="center">
            <Typography.Text
              style={styles.totalDiscountLabel}
            >{`优惠金额: `}</Typography.Text>
            <Typography.Text style={styles.totalDiscount}>{`￥${
              priceInfo == null ? '--' : priceInfo.totalDiscount.toFixed(2)
            }`}</Typography.Text>
          </StackLayout>
        </StackLayout>
        <StackLayout direction="row" gap={0}>
          {form.subjects.length > 0 ? (
            <Center.Touchable
              onPress={onPrint}
              css={`
                background-color: ${StatusColors.Info};
                height: ${rpx(44)}px;
                border-radius: ${rpx(44)}px;
                padding: 0 ${rpx(20)}px;
              `}
            >
              <Typography.Text color="#fff" bold size={rpx(18)}>
                打印报价单
              </Typography.Text>
            </Center.Touchable>
          ) : null}
        </StackLayout>
      </StackLayout>
    );
  },
);

const useStyles = () => {
  const { rpx } = useResponsiveContext();
  const { bottom } = useSafeAreaInsets();
  return useMemo(
    () =>
      StyleSheet.create({
        container: {
          backgroundColor: '#f9f9f9',
          paddingHorizontal: rpx(15),
          paddingBottom: Math.max(rpx(15), bottom * 0.75 + rpx(4)),
          paddingTop: rpx(15),
        },
        totalLabel: {
          fontSize: rpx(12),
          color: '#333',
        },
        total: {
          color: StatusColors.Danger,
          fontSize: rpx(23),
          fontFamily: FontFamily.NotoSans.Black,
          top: rpx(-2),
          fontWeight: '800',
          letterSpacing: rpx(-1.5),
        },
        totalDiscountLabel: {
          color: '#333',
          fontSize: rpx(12),
        },
        totalDiscount: {
          fontSize: rpx(12),
          color: StatusColors.Danger,
        },
      }),
    [bottom, rpx],
  );
};
